import {
  Autocomplete,
  AutocompleteValue,
  Box,
  MenuList,
  styled,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import useCreation from "ahooks/es/useCreation";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  MemberChip,
  MemberMenuItemContent,
} from "../../../../../../global/components";
import {
  StyledListSubheader,
  StyledPopper,
  StyledTeamName,
} from "../../../../../../global/styles";
import { stripDiacritics } from "../../../../../../modules/assets/helperFunctions";
import { createFuse } from "../../../../asset-management/components/Dialogs/DeskManagementDialog/components/DialogInputSection.tsx";
import { GroupMember } from "../../../../asset-management/domain";
import { useAdminOptions } from "../../../hooks";

const MIN_CHARS_TO_SEARCH = 2;
const CHUNK_SIZE = 50;

export type AdminAutocompleteProps = Omit<
  TextFieldProps,
  "value" | "onChange"
> & {
  value: GroupMember | undefined;
  onChange?: (member: GroupMember | undefined) => void;
};

export const Icon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexBasis: "24px",
  width: "24px",
}));

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function (props, ref) {
  const { children, ...other } = props;
  return (
    <MenuList
      component="div"
      ref={ref}
      {...other}
      sx={{ position: "relative", padding: 1 }}
    >
      {children}
    </MenuList>
  );
});

function isOptionEqualToValue(option: GroupMember, value: GroupMember) {
  return option.id === value.id;
}

function groupByGroupName(option: GroupMember) {
  return option.groupName;
}

export const AdminAutocomplete: React.FC<AdminAutocompleteProps> = ({
  value,
  onChange,
  ...props
}) => {
  const { t } = useTranslation("admin-management");
  const options: GroupMember[] = useAdminOptions(value);
  const fuse = useCreation(() => createFuse(options), [options]);

  const [currentInput, setCurrentInput] = React.useState("");
  const deferredInput = React.useDeferredValue(currentInput);

  const [visibleCount, setVisibleCount] = React.useState(CHUNK_SIZE);

  React.useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [deferredInput]);

  const filteredOptionsAll = React.useMemo(() => {
    const raw = deferredInput.trim();
    if (raw.length < MIN_CHARS_TO_SEARCH) {
      return options;
    }
    const query = stripDiacritics(raw);
    const result = fuse.search(query);
    return result.map((it) => it.item);
  }, [deferredInput, options, fuse]);

  const visibleOptions = React.useMemo(
    () => filteredOptionsAll.slice(0, visibleCount),
    [filteredOptionsAll, visibleCount]
  );

  const handleListboxScroll = useMemoizedFn(
    (e: React.UIEvent<HTMLUListElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      if (nearBottom && visibleOptions.length < filteredOptionsAll.length) {
        setVisibleCount((prev) =>
          Math.min(prev + CHUNK_SIZE, filteredOptionsAll.length)
        );
      }
    }
  );

  const handleChange = useMemoizedFn(
    (
      event: React.SyntheticEvent,
      value: AutocompleteValue<GroupMember, undefined, true, undefined>
    ) => {
      event.stopPropagation();
      onChange?.(value);
    }
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={(theme) => ({
          display: "flex",
          width: "100%",
          [theme.breakpoints.down("md")]: {
            marginBottom: 1,
          },
        })}
      >
        <Autocomplete
          fullWidth
          options={visibleOptions}
          value={value}
          onChange={handleChange}
          onInputChange={(_, v) => setCurrentInput(v)}
          groupBy={groupByGroupName}
          isOptionEqualToValue={isOptionEqualToValue}
          disableClearable
          autoHighlight
          filterOptions={(opts) => opts}
          PopperComponent={StyledPopper}
          ListboxComponent={ListboxComponent}
          ListboxProps={{ onScroll: handleListboxScroll }}
          getOptionLabel={(option) => option.user.name.full}
          noOptionsText={t("dialog.form.no-options")}
          renderInput={(params) => <TextField {...params} {...props} />}
          renderTags={(value, getTagProps) =>
            value.map((member, index) => (
              <MemberChip
                label={member.user.name.full}
                {...getTagProps({ index })}
              />
            ))
          }
          renderGroup={({ group, ...props }) => (
            <GroupHeader label={group} {...props} />
          )}
          renderOption={(props, option) => (
            <MemberMenuItemContent
              elementProps={props}
              avatarImageUrl={option.user.avatarImageUrlSrc}
              name={option.user.name.full}
              user={option.user}
            />
          )}
          data-testid="organization-members-autocomplete"
        />
      </Box>
    </Box>
  );
};

interface GroupHeaderProps {
  label: string;
  children?: React.ReactNode;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({ label, children }) => {
  const { t } = useTranslation("admin-management");

  return (
    <div>
      <StyledListSubheader
        sx={{
          position: "sticky",
          top: "0px",
          zIndex: 1,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="body3">{t("dialog.form.team")}</Typography>
        <StyledTeamName>{label}</StyledTeamName>
      </StyledListSubheader>
      <ul style={{ margin: 0, padding: 0 }}>{children}</ul>
    </div>
  );
};
